import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import shirt1 from "../assets/shirt2.png";
import shirt2 from "../assets/shirt1.jpeg";
import shirt3 from "../assets/shirt2.jpeg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&family=Playfair+Display:ital,wght@1,400;1,600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #040008; overflow-x: hidden; }
  input, select { font-family: inherit; }
`;

// ── Keyframes ──────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50%       { transform: translateY(-16px) rotate(2deg); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50%       { opacity: 0.7; transform: scale(1.06); }
`;
const scanDown = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  5%   { opacity: 0.4; }
  95%  { opacity: 0.4; }
  100% { transform: translateY(800%); opacity: 0; }
`;
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;
const grain = keyframes`
  0%   { transform: translate(0, 0); }
  10%  { transform: translate(-2%, -3%); }
  30%  { transform: translate(3%, -1%); }
  50%  { transform: translate(-1%, 3%); }
  70%  { transform: translate(2%, 1%); }
  90%  { transform: translate(-3%, 2%); }
  100% { transform: translate(0, 0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const checkPop = keyframes`
  0%   { transform: scale(0) rotate(-10deg); opacity: 0; }
  60%  { transform: scale(1.15) rotate(3deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Layout ─────────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #040008;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const Grain = styled.div`
  position: fixed;
  inset: -50%;
  width: 200%;
  height: 200%;
  pointer-events: none;
  opacity: 0.045;
  z-index: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  animation: ${grain} 8s steps(1) infinite;
`;
const Orb = styled.div`
  position: fixed;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || "120px"});
  pointer-events: none;
  opacity: ${({ op }) => op || 0.15};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  animation: ${pulse} ${({ dur }) => dur || "5s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
`;
const ScanLine = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 247, 0.3),
    transparent
  );
  pointer-events: none;
  z-index: 5;
  animation: ${scanDown} 6s linear infinite;
`;

// ── Nav ────────────────────────────────────────────────────────────────────────

const BackBtn = styled.button`
  position: fixed;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 100px;
  padding: 7px 14px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  opacity: 0;
  transition:
    background 0.2s,
    color 0.2s,
    border-color 0.2s;
  &:hover {
    background: rgba(168, 85, 247, 0.08);
    border-color: rgba(168, 85, 247, 0.25);
    color: rgba(255, 255, 255, 0.6);
  }
`;

// ── Two-column main ────────────────────────────────────────────────────────────

const Main = styled.main`
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: clamp(2rem, 5vw, 5rem);
  padding: clamp(5rem, 10vh, 7rem) clamp(2rem, 8vw, 7rem);

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 5rem 1.5rem 3rem;
    gap: 2.5rem;
  }
`;

// ── Left column ────────────────────────────────────────────────────────────────

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1.2rem, 2.5vh, 1.8rem);
  opacity: 0;
`;
const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
`;
const TagDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #a855f7;
  box-shadow: 0 0 8px #a855f7;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;
const Headline = styled.h1`
  font-family: "Bebas Neue", sans-serif;
  font-size: clamp(3.5rem, 9vw, 9rem);
  line-height: 0.88;
  letter-spacing: 0.02em;
  color: #fff;
  .line2 {
    display: block;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;
  }
`;
const Sub = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: clamp(0.9rem, 1.6vw, 1.05rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  line-height: 1.9;
  max-width: 420px;
  em {
    font-style: italic;
    color: rgba(255, 255, 255, 0.6);
  }
  @media (max-width: 780px) {
    max-width: 100%;
  }
`;
const ChipRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  @media (max-width: 780px) {
    justify-content: center;
  }
`;
const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-family: "DM Sans", sans-serif;
  font-size: 0.67rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.04em;
`;

// ── Preorder info card ─────────────────────────────────────────────────────────

const NoticeCard = styled.div`
  background: rgba(124, 58, 237, 0.06);
  border: 1px solid rgba(168, 85, 247, 0.18);
  border-radius: 16px;
  padding: 1.1rem 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const NoticeTitle = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #a855f7;
`;
const NoticeText = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.8rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.7;
`;

// ── Form ───────────────────────────────────────────────────────────────────────

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  ${({ $full }) => $full && "grid-column: 1 / -1;"}
`;
const FieldLabel = styled.label`
  font-family: "DM Sans", sans-serif;
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
  text-align: left;
`;
const fieldBase = `
  width: 100%;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 0.7rem 1rem;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem; font-weight: 300;
  color: rgba(255,255,255,0.85);
  outline: none;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  -webkit-appearance: none;
  &::placeholder { color: rgba(255,255,255,0.15); }
  &:focus {
    border-color: rgba(168,85,247,0.5);
    background: rgba(168,85,247,0.05);
    box-shadow: 0 0 0 3px rgba(168,85,247,0.08);
  }
`;
const FInput = styled.input`
  ${fieldBase}
`;
const ErrorMsg = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.75rem;
  color: #f87171;
  text-align: center;
`;

// ── CTA ────────────────────────────────────────────────────────────────────────

const CtaGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  @media (max-width: 780px) {
    align-items: center;
  }
`;
const CtaBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  padding: 0 clamp(2rem, 4vw, 3rem);
  height: clamp(52px, 7vh, 62px);
  border-radius: 100px;
  border: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: clamp(0.85rem, 1.3vw, 0.95rem);
  font-weight: 500;
  letter-spacing: 0.1em;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #a855f7, #c026d3);
  background-size: 200% auto;
  box-shadow:
    0 8px 32px rgba(124, 58, 237, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background-position 0.4s;
  overflow: hidden;
  width: fit-content;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 12%;
    right: 12%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    border-radius: 50%;
  }
  &:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.02);
    box-shadow:
      0 18px 50px rgba(124, 58, 237, 0.55),
      0 0 0 1px rgba(255, 255, 255, 0.1) inset;
    background-position: right center;
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
const ArrowSpan = styled.span`
  display: inline-block;
  transition: transform 0.25s ease;
  ${CtaBtn}:hover:not(:disabled) & {
    transform: translateX(4px);
  }
`;
const Spinner = styled.div`
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;
const Disclaimer = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.62rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.15);
  line-height: 1.6;
  span {
    display: inline-block;
    width: 4px;
    height: 0.7em;
    background: rgba(168, 85, 247, 0.6);
    vertical-align: middle;
    margin-left: 3px;
    animation: ${blink} 1s step-end infinite;
  }
`;

// ── Right column ───────────────────────────────────────────────────────────────

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  opacity: 0;
  @media (max-width: 780px) {
    order: -1;
  }
`;

// ── Slider ─────────────────────────────────────────────────────────────────────

const sliderFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.97) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const SliderWrap = styled.div`
  position: relative;
  width: clamp(240px, 36vw, 400px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.4rem;
`;
const SliderStage = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SliderImg = styled.img`
  width: 100%;
  height: clamp(260px, 38vw, 420px);
  object-fit: contain;
  display: block;
  animation:
    ${sliderFadeIn} 0.45s cubic-bezier(0.34, 1.2, 0.64, 1) both,
    ${float} 5s ease-in-out 0.45s infinite;
  filter: drop-shadow(0 30px 60px rgba(168, 85, 247, 0.42));
`;
const ShirtBadge = styled.div`
  position: absolute;
  top: -10px;
  right: -4px;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e879f9, #a855f7);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  box-shadow: 0 0 24px rgba(232, 121, 249, 0.55);
  animation: ${pulse} 2s ease-in-out infinite;
  z-index: 2;
`;
const ShirtBadgeText = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.42rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #fff;
  line-height: 1.2;
  text-align: center;
`;
const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $dir }) => ($dir === "left" ? "left: -18px;" : "right: -18px;")}
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.2s,
    border-color 0.2s,
    color 0.2s,
    transform 0.2s;
  z-index: 3;
  &:hover {
    background: rgba(168, 85, 247, 0.15);
    border-color: rgba(168, 85, 247, 0.4);
    color: #fff;
    transform: translateY(-50%) scale(1.08);
  }
`;
const Dots = styled.div`
  display: flex;
  gap: 7px;
  align-items: center;
`;
const Dot = styled.button`
  width: ${({ $active }) => ($active ? "22px" : "7px")};
  height: 7px;
  border-radius: 100px;
  background: ${({ $active }) =>
    $active ? "#a855f7" : "rgba(255,255,255,0.18)"};
  border: none;
  cursor: pointer;
  padding: 0;
  transition:
    width 0.3s ease,
    background 0.3s ease;
  box-shadow: ${({ $active }) =>
    $active ? "0 0 10px rgba(168,85,247,0.5)" : "none"};
`;

// ── Size picker ────────────────────────────────────────────────────────────────

const SizeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  width: 100%;
  max-width: 340px;
`;
const SizeLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
`;
const SizeRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;
const SizeBtn = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(168,85,247,0.6)" : "rgba(255,255,255,0.08)"};
  background: ${({ $active }) =>
    $active ? "rgba(124,58,237,0.18)" : "rgba(255,255,255,0.03)"};
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: ${({ $active }) => ($active ? "500" : "300")};
  color: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)"};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active ? "0 0 18px rgba(168,85,247,0.25)" : "none"};
  &:hover {
    border-color: rgba(168, 85, 247, 0.4);
    color: rgba(255, 255, 255, 0.7);
    background: rgba(124, 58, 237, 0.08);
  }
`;
const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  justify-content: center;
`;
const Price = styled.div`
  font-family: "Bebas Neue", sans-serif;
  font-size: 2.8rem;
  color: #fff;
  letter-spacing: 0.04em;
`;
const PriceSub = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.28);
`;

// ── Success state ──────────────────────────────────────────────────────────────

const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.7rem;
  animation: ${fadeUp} 0.5s ease-out;
  @media (max-width: 780px) {
    align-items: center;
  }
`;
const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  animation: ${checkPop} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
`;
const SuccessTitle = styled.div`
  font-family: "Bebas Neue", sans-serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  color: #fff;
  line-height: 1;
`;
const SuccessBody = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.75;
  max-width: 380px;
`;
const RedirectNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(168, 85, 247, 0.7);
`;
const RedirectDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #a855f7;
  box-shadow: 0 0 8px #a855f7;
  animation: ${pulse} 1s ease-in-out infinite;
`;

// ── Constants ──────────────────────────────────────────────────────────────────

const STRIPE_LINK = "https://buy.stripe.com/test_7sY5kC66H5jg5vbgowbZe00";
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

// ── Component ──────────────────────────────────────────────────────────────────

export default function PreorderPage() {
  const navigate = useNavigate();

  const backRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  const [selectedSize, setSelectedSize] = useState("M");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [slide, setSlide] = useState(0);
  const [slideKey, setSlideKey] = useState(0); // forces re-mount for animation

  const SHIRTS = [shirt1, shirt3, shirt2];
  const SHIRT_LABELS = ["front view", "back view", "detail"];

  const goTo = (i) => {
    setSlide(i);
    setSlideKey((k) => k + 1);
  };
  const prev = () => goTo((slide + SHIRTS.length - 1) % SHIRTS.length);
  const next = () => goTo((slide + 1) % SHIRTS.length);

  useEffect(() => {
    gsap
      .timeline({ delay: 0.05 })
      .to(backRef.current, { opacity: 1, duration: 0.5, ease: "power2.out" }, 0)
      .to(
        leftRef.current,
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" },
        0.1,
      )
      .to(
        rightRef.current,
        { opacity: 1, y: 0, duration: 0.8, ease: "back.out(1.4)" },
        0.2,
      );
  }, []);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      await addDoc(collection(db, "preorders"), {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        size: selectedSize,
        status: "pending_payment",
        createdAt: serverTimestamp(),
      });

      setSuccess(true);

      // Brief confirmation moment, then send to Stripe
      setTimeout(() => {
        window.location.href = STRIPE_LINK;
      }, 2200);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <Grain />
        <ScanLine />

        <Orb
          s="500px"
          c="rgba(124,58,237,0.22)"
          top="-10%"
          left="-8%"
          op={0.22}
          dur="6s"
          del="0s"
          blur="130px"
        />
        <Orb
          s="400px"
          c="rgba(232,121,249,0.18)"
          bottom="-5%"
          right="-6%"
          op={0.18}
          dur="7s"
          del="1.5s"
          blur="110px"
        />
        <Orb
          s="280px"
          c="rgba(168,85,247,0.12)"
          top="45%"
          left="42%"
          op={0.1}
          dur="5.5s"
          del="0.8s"
          blur="100px"
        />

        <BackBtn ref={backRef} onClick={() => navigate(-1)}>
          ← back
        </BackBtn>

        <Main>
          {/* ── Left: copy + form */}
          <LeftCol ref={leftRef} style={{ transform: "translateY(30px)" }}>
            <Tag>
              <TagDot />
              pre-order now · limited run
            </Tag>

            <Headline>
              THE NUDR
              <br />
              <span className="line2">SHIRT.</span>
            </Headline>

            <Sub>
              you saw it coming. <em>here it is.</em>
              <br />
              unisex heavyweight cotton, NudR logo, built for the ones who
              actually get it. limited first drop — once they're gone, they're
              gone.
            </Sub>

            <ChipRow>
              <Chip>🖤 unisex fit</Chip>
              <Chip>🧵 heavyweight cotton</Chip>
              <Chip>🔒 limited drop</Chip>
              <Chip>📦 ships across all US</Chip>
            </ChipRow>

            <NoticeCard>
              <NoticeTitle>📦 Pre-Order Info</NoticeTitle>
              <NoticeText>
                Your card is charged on the next step to secure your spot.
                Shirts are in production — we'll ship when the drop is ready.
                You'll get an email confirmation and tracking when it's on the
                way.
              </NoticeText>
            </NoticeCard>

            {success ? (
              <SuccessWrap>
                <SuccessIcon>🎉</SuccessIcon>
                <SuccessTitle>YOU'RE IN.</SuccessTitle>
                <SuccessBody>
                  Your size{" "}
                  <strong style={{ color: "rgba(168,85,247,0.9)" }}>
                    {selectedSize}
                  </strong>{" "}
                  is reserved. Taking you to payment now to lock it in.
                </SuccessBody>
                <RedirectNote>
                  <RedirectDot />
                  redirecting to secure checkout…
                </RedirectNote>
              </SuccessWrap>
            ) : (
              <form
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <FormRow>
                  <FieldWrap>
                    <FieldLabel htmlFor="po-name">Full name *</FieldLabel>
                    <FInput
                      id="po-name"
                      name="name"
                      type="text"
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel htmlFor="po-phone">Phone (optional)</FieldLabel>
                    <FInput
                      id="po-phone"
                      name="phone"
                      type="tel"
                      placeholder="+1 555 000 0000"
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </FieldWrap>
                  <FieldWrap $full>
                    <FieldLabel htmlFor="po-email">Email address *</FieldLabel>
                    <FInput
                      id="po-email"
                      name="email"
                      type="email"
                      placeholder="jane@email.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </FieldWrap>
                </FormRow>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <CtaGroup>
                  <CtaBtn type="submit" disabled={loading}>
                    {loading && <Spinner />}
                    {loading
                      ? "saving your spot…"
                      : "pre-order · secure your shirt"}
                    {!loading && <ArrowSpan>→</ArrowSpan>}
                  </CtaBtn>
                  <Disclaimer>
                    we save your details, then redirect to secure stripe
                    checkout. no hidden fees.
                    <span />
                  </Disclaimer>
                </CtaGroup>
              </form>
            )}
          </LeftCol>

          {/* ── Right: slider + size + price */}
          <RightCol ref={rightRef} style={{ transform: "translateY(30px)" }}>
            <SliderWrap>
              <SliderStage>
                <ArrowBtn
                  $dir="left"
                  type="button"
                  onClick={prev}
                  aria-label="Previous"
                >
                  ‹
                </ArrowBtn>

                <SliderImg
                  key={slideKey}
                  src={SHIRTS[slide]}
                  alt={`NudR shirt — ${SHIRT_LABELS[slide]}`}
                  draggable="false"
                />

                <ShirtBadge>
                  <ShirtBadgeText>
                    limited
                    <br />
                    drop
                  </ShirtBadgeText>
                </ShirtBadge>

                <ArrowBtn
                  $dir="right"
                  type="button"
                  onClick={next}
                  aria-label="Next"
                >
                  ›
                </ArrowBtn>
              </SliderStage>

              <Dots>
                {SHIRTS.map((_, i) => (
                  <Dot
                    key={i}
                    $active={slide === i}
                    onClick={() => goTo(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </Dots>
            </SliderWrap>

            <PriceRow>
              <Price>$24.99</Price>
              {/* <PriceSub>+ free shipping</PriceSub> */}
            </PriceRow>

            <SizeSection>
              <SizeLabel>select your size</SizeLabel>
              <SizeRow>
                {SIZES.map((s) => (
                  <SizeBtn
                    key={s}
                    type="button"
                    $active={selectedSize === s}
                    onClick={() => setSelectedSize(s)}
                  >
                    {s}
                  </SizeBtn>
                ))}
              </SizeRow>
            </SizeSection>
          </RightCol>
        </Main>
      </Page>
    </>
  );
}
